/**
 * MemberRolesPanel — Muestra los cargos actuales de un miembro dentro del
 * club y permite añadir/desactivar cargos (Fase A del multi-cargo).
 *
 * Visibilidad y permisos:
 * - El panel se muestra siempre que exista `memberId` y `clubId` (miembro ya
 *   persistido). No aparece en creación.
 * - Añadir/desactivar cargos requiere `roles.write` en el contexto del club
 *   — se guarda con `<Can>` del RBAC dinámico.
 *
 * Sincronización legacy:
 * - Al crear/desactivar cargos, el servicio actualiza `member.role` y
 *   `member.staffSection` con el cargo principal (de mayor jerarquía). Eso
 *   preserva la compatibilidad con toda la UI actual que aún lee esos campos.
 */
import { useMemo, useState } from "react";
import Button from "@/components/ui/Button";
import { RoleBadge } from "@/components/ui";
import { Can } from "@/components/auth/Can";
import { sortSections } from "@/lib/constants/sections";
import {
  isRoleAllowedInSection,
  roleSectionRestrictionMessage,
} from "@/lib/constants/roles";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useMemberRoles,
  useAssignRole,
  useDeactivateAssignment,
  useHardDeleteAssignment,
  useUpdateAssignment,
} from "@/hooks/useMemberRoles";
import type { ClubType, MemberRole, MemberRoleAssignment } from "@/types";
import { CLUB_TYPE_LABELS, MEMBER_ROLE_LABELS } from "@/types";
import {
  ChevronRight,
  Pencil,
  Trash2,
  Info,
  AlertTriangle,
} from "lucide-react";

interface MemberRolesPanelProps {
  memberId: string;
  personId: string;
  clubId: string;
  clubSections: ClubType[];
  personName?: string;
}

// Todas las opciones de rol EXCEPTO "member" (no se representa aquí).
const ROLE_OPTIONS: MemberRole[] = [
  "director",
  "subdirector",
  "consejero",
  "anciano",
  "instructor",
  "tesorero",
  "secretario",
];

export function MemberRolesPanel({
  memberId,
  personId,
  clubId,
  clubSections,
  personName,
}: MemberRolesPanelProps) {
  const profile = useAuthStore((s) => s.profile);
  const { canManageSection } = usePermissions();
  const { data: assignments = [], isLoading } = useMemberRoles(memberId);
  const assignMut = useAssignRole();
  const deactivateMut = useDeactivateAssignment();
  const updateMut = useUpdateAssignment();
  const hardDeleteMut = useHardDeleteAssignment();

  const active = useMemo(
    () => assignments.filter((a) => a.status === "active"),
    [assignments],
  );
  const history = useMemo(
    () => assignments.filter((a) => a.status !== "active"),
    [assignments],
  );

  // Secciones que el usuario actual puede gestionar en este club.
  // Un director de sección solo ve/gestiona su sección; el general y los
  // admins ven todas.
  const manageableSections = useMemo(
    () => sortSections(clubSections).filter((s) => canManageSection(clubId, s)),
    [clubSections, clubId, canManageSection],
  );

  const canManageAny = manageableSections.length > 0;

  const [showForm, setShowForm] = useState(false);
  const [formRole, setFormRole] = useState<MemberRole>("director");
  const [formSection, setFormSection] = useState<ClubType>(
    manageableSections[0] ?? clubSections[0] ?? "aventureros",
  );
  const [formError, setFormError] = useState<string | null>(null);

  // Edición inline de un cargo activo existente.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<MemberRole>("director");
  const [editSection, setEditSection] = useState<ClubType>("aventureros");
  const [editError, setEditError] = useState<string | null>(null);

  const startEdit = (a: MemberRoleAssignment) => {
    setEditingId(a.id);
    setEditRole(a.role);
    setEditSection(a.section);
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError(null);
  };

  const handleSaveEdit = async (a: MemberRoleAssignment) => {
    if (!profile?.id) return;
    if (!manageableSections.includes(editSection)) {
      setEditError(
        `No puedes mover este cargo a ${CLUB_TYPE_LABELS[editSection]}: no gestionas esa sección.`,
      );
      return;
    }
    try {
      await updateMut.mutateAsync({
        assignmentId: a.id,
        role: editRole,
        section: editSection,
        userId: profile.id,
        userName: profile.displayName,
      });
      cancelEdit();
    } catch (err) {
      setEditError(
        err instanceof Error ? err.message : "Error al actualizar el cargo.",
      );
    }
  };

  const handleHardDelete = async (a: MemberRoleAssignment) => {
    if (!profile?.id) return;
    const label = `${MEMBER_ROLE_LABELS[a.role]} de ${CLUB_TYPE_LABELS[a.section]}`;
    const ok = confirm(
      `ELIMINAR "${label}" definitivamente.\n\n` +
        `Úsalo solo si el cargo fue un error (ej: sección equivocada).\n` +
        `Si el miembro sí ocupó el cargo y ahora terminó, usa "Terminar" para conservar el historial.\n\n` +
        `¿Eliminar sin dejar rastro?`,
    );
    if (!ok) return;
    try {
      await hardDeleteMut.mutateAsync({
        assignmentId: a.id,
        userId: profile.id,
      });
    } catch (err) {
      alert(
        `No se pudo eliminar: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const handleAdd = async () => {
    setFormError(null);
    if (!profile?.id) {
      setFormError("No hay sesión activa.");
      return;
    }
    // Guard: no permitir crear en secciones que el usuario no gestiona.
    if (!manageableSections.includes(formSection)) {
      setFormError(
        `No puedes asignar cargos en ${CLUB_TYPE_LABELS[formSection]}: solo el director general o el director de esa sección puede hacerlo.`,
      );
      return;
    }
    // Rechazo local: ya existe activa con misma tripleta
    const dupe = active.find(
      (a) => a.role === formRole && a.section === formSection,
    );
    if (dupe) {
      setFormError(
        `Este miembro ya es ${MEMBER_ROLE_LABELS[formRole]} de ${CLUB_TYPE_LABELS[formSection]}.`,
      );
      return;
    }
    try {
      await assignMut.mutateAsync({
        memberId,
        personId,
        clubId,
        section: formSection,
        role: formRole,
        assignedBy: profile.id,
        assignedByName: profile.displayName,
        personName,
      });
      setShowForm(false);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Error al asignar el cargo.",
      );
    }
  };

  const handleDeactivate = async (a: MemberRoleAssignment) => {
    if (!profile?.id) return;
    if (
      !confirm(
        `¿Terminar el cargo "${MEMBER_ROLE_LABELS[a.role]}" en ${CLUB_TYPE_LABELS[a.section]}? El registro queda en el historial.`,
      )
    ) {
      return;
    }
    try {
      await deactivateMut.mutateAsync({
        assignmentId: a.id,
        userId: profile.id,
        userName: profile.displayName,
      });
    } catch (err) {
      alert(
        `No se pudo desactivar: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  };

  return (
    <div className="space-y-4 border-t border-gray-200 pt-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">
            Cargos en el club
          </h4>
          <p className="mt-0.5 text-xs text-gray-500">
            Una persona puede tener varios cargos activos a la vez (por ejemplo
            directora de una sección y tesorera de otra).
          </p>
        </div>
        {canManageAny && (
          <Can permission="roles.write" clubId={clubId}>
            <Button
              type="button"
              variant={showForm ? "ghost" : "outline"}
              size="sm"
              onClick={() => {
                setShowForm((v) => !v);
                setFormError(null);
              }}
            >
              {showForm ? "Cancelar" : "Añadir cargo"}
            </Button>
          </Can>
        )}
      </div>

      {showForm && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Cargo
              </span>
              <select
                value={formRole}
                onChange={(e) => setFormRole(e.target.value as MemberRole)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {ROLE_OPTIONS.filter((r) =>
                  isRoleAllowedInSection(r, formSection),
                ).map((r) => (
                  <option key={r} value={r}>
                    {MEMBER_ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Sección
              </span>
              <select
                value={formSection}
                onChange={(e) => {
                  const nextSection = e.target.value as ClubType;
                  setFormSection(nextSection);
                  // Si el rol actual ya no es válido en la nueva sección,
                  // cambiarlo automáticamente al primer válido.
                  if (!isRoleAllowedInSection(formRole, nextSection)) {
                    const fallback = ROLE_OPTIONS.find((r) =>
                      isRoleAllowedInSection(r, nextSection),
                    );
                    if (fallback) setFormRole(fallback);
                  }
                }}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {manageableSections.map((s) => (
                  <option key={s} value={s}>
                    {CLUB_TYPE_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {/* Hint dinámico por combinaciones prohibidas (ej: Consejero en GM) */}
          {roleSectionRestrictionMessage(formRole, formSection) && (
            <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
              <span>
                {roleSectionRestrictionMessage(formRole, formSection)}
              </span>
            </p>
          )}
          {formError && (
            <p className="mt-2 text-xs text-red-600">{formError}</p>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowForm(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              loading={assignMut.isPending}
              onClick={handleAdd}
            >
              Guardar cargo
            </Button>
          </div>
        </div>
      )}

      {isLoading && <p className="text-sm text-gray-500">Cargando cargos…</p>}

      {!isLoading && active.length === 0 && (
        <p className="text-sm text-gray-500">
          Este miembro aún no tiene cargos asignados en este club.
        </p>
      )}

      {active.length > 0 && (
        <ul className="space-y-2">
          {active.map((a) => {
            const isEditing = editingId === a.id;
            return (
              <li
                key={a.id}
                className={`rounded-lg border px-3 py-2 text-sm shadow-sm ${
                  !isRoleAllowedInSection(a.role, a.section)
                    ? "border-amber-300 bg-amber-50/40"
                    : "border-gray-200 bg-white"
                }`}
              >
                {!isEditing ? (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-col gap-1">
                      <RoleBadge
                        role={a.role}
                        section={a.section}
                        showSection
                        size="sm"
                      />
                      {!isRoleAllowedInSection(a.role, a.section) && (
                        <span className="text-[11px] text-amber-700 font-medium flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Combinación no permitida — corrige o elimina
                        </span>
                      )}
                    </div>
                    <Can permission="roles.write" clubId={clubId}>
                      {(manageableSections.includes(a.section) ||
                        !isRoleAllowedInSection(a.role, a.section)) && (
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={() => startEdit(a)}
                            title="Editar cargo o sección"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            <span className="ml-1 hidden sm:inline">
                              Editar
                            </span>
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={() => handleDeactivate(a)}
                            title="Terminar cargo (queda en historial)"
                          >
                            Terminar
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={() => handleHardDelete(a)}
                            title="Eliminar por error (sin historial)"
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </Can>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid gap-2 sm:grid-cols-2">
                      <label className="text-xs">
                        <span className="mb-1 block font-medium text-gray-700">
                          Cargo
                        </span>
                        <select
                          value={editRole}
                          onChange={(e) =>
                            setEditRole(e.target.value as MemberRole)
                          }
                          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                          {ROLE_OPTIONS.filter((r) =>
                            isRoleAllowedInSection(r, editSection),
                          ).map((r) => (
                            <option key={r} value={r}>
                              {MEMBER_ROLE_LABELS[r]}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs">
                        <span className="mb-1 block font-medium text-gray-700">
                          Sección
                        </span>
                        <select
                          value={editSection}
                          onChange={(e) => {
                            const nextSection = e.target.value as ClubType;
                            setEditSection(nextSection);
                            if (
                              !isRoleAllowedInSection(editRole, nextSection)
                            ) {
                              const fallback = ROLE_OPTIONS.find((r) =>
                                isRoleAllowedInSection(r, nextSection),
                              );
                              if (fallback) setEditRole(fallback);
                            }
                          }}
                          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                          {manageableSections.map((s) => (
                            <option key={s} value={s}>
                              {CLUB_TYPE_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    {roleSectionRestrictionMessage(editRole, editSection) && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 flex items-start gap-1.5">
                        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
                        <span>
                          {roleSectionRestrictionMessage(editRole, editSection)}
                        </span>
                      </p>
                    )}
                    {editError && (
                      <p className="text-xs text-red-600">{editError}</p>
                    )}
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        onClick={cancelEdit}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        size="xs"
                        loading={updateMut.isPending}
                        onClick={() => handleSaveEdit(a)}
                      >
                        Guardar cambios
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {history.length > 0 && (
        <details className="mt-3 rounded-lg border border-dashed border-gray-200 bg-gray-50/60 [&[open]_.chev]:rotate-90">
          <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-800 flex items-center gap-1.5">
            <ChevronRight className="chev w-3.5 h-3.5 transition-transform" />
            <span>Cargos terminados ({history.length})</span>
          </summary>
          <ul className="px-3 pb-3 pt-1 space-y-1.5">
            {history.map((a) => (
              <li
                key={a.id}
                className="flex items-center gap-2 opacity-60"
                title={`Terminado — ${MEMBER_ROLE_LABELS[a.role] ?? a.role}, ${CLUB_TYPE_LABELS[a.section] ?? a.section}`}
              >
                <RoleBadge
                  role={a.role}
                  section={a.section}
                  showSection
                  size="xs"
                  variant="soft"
                />
                <span className="text-[10px] font-medium uppercase tracking-wider text-gray-500 line-through decoration-2">
                  Terminado
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
